import { claimflowApiClient } from './claimflowApiClient';

interface ErpProxyResponse<T> {
  success: boolean;
  http_status: number;
  data: T;
  error?: { code: string; message: string } | null;
}

export interface VeosComment {
  LIGN_NSIN: string;
  AUTEUR: string;
  INITIALES: string;
  DTCRE_US: string;
  NONLU: number;
  ID_NSIN: number;
  ID_EVENT?: number;
}

interface VeosCommentsResponse {
  total: number;
  data: VeosComment[];
}

interface VeosCommentCounterRow {
  NB: number;
  NONLU: number;
}

interface VeosCommentCounterResponse {
  total: number;
  data: VeosCommentCounterRow[];
}

export interface VeosPlanificateur {
  ID_PLANIF: number;
  NOM: string;
}

interface VeosPlanificateurResponse {
  total: number;
  data: VeosPlanificateur[];
}

export interface CommentCounter {
  total: number;
  unread: number;
}

export interface PostCommentWithMentionsInput {
  ses: string | number;
  idSin: string | number;
  ncbtSin: string;
  comment: string;
  creator: string;
  mentions: number[];
}

export const commentsService = {
  async fetchComments(
    ses: string | number,
    idSin: string | number,
  ): Promise<VeosComment[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosCommentsResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-mod-commentaires',
      { params: { SES: ses, ID_SIN: idSin, perPage: 1_000_000 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async fetchCommentCounter(
    ses: string | number,
    idSin: string | number,
  ): Promise<CommentCounter> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosCommentCounterResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-ctr-commentaires',
      { params: { SES: ses, ID_SIN: idSin } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    const first = response.data.data.data[0];
    return {
      total: first?.NB ?? 0,
      unread: first?.NONLU ?? 0,
    };
  },

  async searchPlanificateurs(
    ses: string | number,
    q: string,
  ): Promise<VeosPlanificateur[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosPlanificateurResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-planificateur',
      { params: { SES: ses, Q: q, perPage: 50 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async postCommentWithMentions(input: PostCommentWithMentionsInput): Promise<void> {
    await claimflowApiClient.post(
      '/claimflow/workflows/claimflow-post-comment/execute',
      {
        context_data: {
          ses: input.ses,
          id_sin: input.idSin,
          ncbt_sin: input.ncbtSin,
          comment: input.comment,
          creator: input.creator,
          mentions: input.mentions.map((id) => ({ scheduler_id: String(id) })),
        },
      },
    );
  },

  async markCommentRead(idTask: number): Promise<void> {
    const response = await claimflowApiClient.post<ErpProxyResponse<unknown>>(
      '/claimflow/erp/registries/veos/endpoints/task-update',
      { data: { idTask: String(idTask), maxProgress: '100' } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }
  },
};
